// «Ρωτήστε το AVRASTRO» (owner, 16 Oct 2026: «… και για το CRM») — questions about USING the CRM, answered from its
// own help articles, lessons and «What's new», in the advisor's language (el / en / ru), with links to the exact
// article, lesson or screen.
//   AskHelp          the panel: the warning not to paste client details, the question (redacted on this device before
//                    it is sent: e-mails, phone numbers, identity / policy numbers, IBANs — a live preview says what
//                    goes), the answer labelled as AI with its sources, and the help search when the AI is not there
//   openAskHelp      the launcher (top bar button, Ctrl+J / ⌘J, and a tab of the help centre)
//   HelpAssistantCard  Settings: on / off for the workspace (setting `help_assistant.enabled`, default on)
// Sent to the platform (POST /v1/assistant/help, signed-in session): the redacted question and the matching help
// sections — never a record of the workspace. Without a signed-in account, without the platform's AI or when the
// workspace turned it off, the same panel answers from the help search alone (nothing leaves the device).
import { useEffect, useMemo, useRef, useState } from 'react';
import { getSetting, saveSetting } from '@vero/domain';
import { PlatformError } from '@vero/platform';
import { useRuntime, type Runtime } from '../core/runtime.tsx';
import { Modal } from '../core/ui.tsx';
import { Icon } from '../core/icons.tsx';
import { navigate } from '../core/router.ts';
import { accountService } from '../core/account-service.ts';
import { useAccount } from './account.tsx';
import { searchArticles } from '../guide/articles.ts';
import { lang3 } from '../guide/l3.ts';
import { helpSections, mentionsOwnHealth, pickHelp, redactQuestion, refTarget, refTitle, removedCount } from '../guide/ask-help.ts';

export const HELP_ASSISTANT_KEY = 'help_assistant.enabled';
type Reason = 'not_signed' | 'not_configured' | 'disabled' | 'failed' | 'rate' | 'health';
type Turn = { id: number; q: string; removed: number; pending: boolean; ai: boolean; answer: string; refs: string[]; reason: Reason | null };

/** is the AI there for this workspace? null while asking the platform */
function useHelpAi(): { ok: boolean | null; reason: Reason | null } {
  const rt = useRuntime();
  const a = useAccount();
  const enabled = getSetting<boolean>(rt.view, HELP_ASSISTANT_KEY, true) !== false;
  const [st, setSt] = useState<{ ok: boolean | null; reason: Reason | null }>({ ok: null, reason: null });
  useEffect(() => {
    if (!enabled) { setSt({ ok: false, reason: 'disabled' }); return; }
    if (!a?.signedIn) { setSt({ ok: false, reason: 'not_signed' }); return; } // (the api throws when signed out)
    const api = accountService()?.api;
    if (!api) { setSt({ ok: false, reason: 'not_signed' }); return; }
    let on = true;
    void api.helpAssistantStatus().then((s) => { if (on) setSt(s.available ? { ok: true, reason: null } : { ok: false, reason: 'not_configured' }); })
      .catch(() => { if (on) setSt({ ok: false, reason: 'not_configured' }); });
    return () => { on = false; };
  }, [enabled, a?.signedIn]);
  return st;
}

export function AskHelp({ inModal }: { inModal?: boolean }) {
  const rt = useRuntime();
  const { t } = rt;
  const lang = lang3(rt.locale);
  const ai = useHelpAi();
  const [q, setQ] = useState('');
  const [turns, setTurns] = useState<Turn[]>([]);
  const seq = useRef(0);
  const input = useRef<HTMLTextAreaElement | null>(null);
  const sections = useMemo(() => helpSections(lang, (k) => t(k)), [lang]);
  const preview = useMemo(() => redactQuestion(q), [q]);
  const nRemoved = removedCount(preview);

  const ask = async (raw: string) => {
    const text = raw.replace(/\s+/g, ' ').trim();
    if (text.length < 3) { input.current?.focus(); return; }
    const red = redactQuestion(text);
    const id = ++seq.current;
    // someone's own health is never sent — answered here
    if (mentionsOwnHealth(text)) { setTurns((x) => [{ id, q: red.text, removed: removedCount(red), pending: false, ai: false, answer: '', refs: [], reason: 'health' as const }, ...x].slice(0, 20)); setQ(''); return; }
    const picked = pickHelp(red.text, sections, 4);
    const turn: Turn = { id, q: red.text, removed: removedCount(red), pending: true, ai: false, answer: '', refs: picked.map((s) => s.ref), reason: null };
    setTurns((x) => [turn, ...x].slice(0, 20));
    setQ('');
    const done = (patch: Partial<Turn>) => setTurns((x) => x.map((y) => (y.id === id ? { ...y, ...patch, pending: false } : y)));
    const api = ai.ok ? accountService()?.api : null;
    // nothing the help covers → no call at all; not available → the help search alone
    if (!ai.ok || !api || !picked.length) { done({ reason: ai.ok ? null : ai.reason ?? 'not_configured' }); return; }
    try {
      const r = await api.helpAssistant({ question: red.text, lang, sections: picked.map(({ ref, title, text }) => ({ ref, title, text })) });
      if (r.outcome === 'answered') done({ ai: true, answer: r.answer, refs: r.refs.length ? r.refs : picked.map((s) => s.ref) });
      else done({ ai: true, answer: r.answer, refs: r.outcome === 'off_topic' || r.outcome === 'sensitive' ? [] : picked.map((s) => s.ref) });
    } catch (e) {
      done({ reason: e instanceof PlatformError && e.status === 429 ? 'rate' : e instanceof PlatformError && e.status === 503 ? 'not_configured' : 'failed' });
    }
  };

  return (
    <div className={`askhelp${inModal ? ' askhelp--modal' : ''}`} data-testid="ask-help">
      {!inModal && <div className="row" style={{ gap: 8, alignItems: 'baseline' }}><h2 style={{ margin: 0 }}>{t('askhelp.title')}</h2><span className="chip askhelp-ai">{t('askhelp.ai_badge')}</span></div>}
      <p className="muted small" style={{ marginTop: inModal ? 0 : 6 }}>{t('askhelp.subtitle')}</p>
      <p className="callout small askhelp-warning" role="note" data-testid="ask-help-warning"><Icon name="shield" size={14} /> {t('askhelp.warning')}</p>
      <form className="askhelp-form" onSubmit={(e: { preventDefault(): void }) => { e.preventDefault(); void ask(q); }}>
        <label className="small" htmlFor="askhelp-q" style={{ fontWeight: 600 }}>{t('askhelp.label')}</label>
        <div className="row" style={{ gap: 8, alignItems: 'flex-end' }}>
          <textarea id="askhelp-q" ref={input} className="input grow" rows={2} maxLength={500} value={q} placeholder={t('askhelp.placeholder')} autoFocus
            onChange={(e: { target: HTMLTextAreaElement }) => setQ(e.target.value)}
            onKeyDown={(e: { key: string; shiftKey: boolean; preventDefault(): void }) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void ask(q); } }}
            aria-describedby="askhelp-redact askhelp-disclosure" data-testid="ask-help-input" />
          <button className="btn btn-primary" type="submit" disabled={q.trim().length < 3} data-testid="ask-help-send"><Icon name="spark" />{t('askhelp.ask')}</button>
        </div>
        <p id="askhelp-redact" className="tiny askhelp-redact" aria-live="polite" data-testid="ask-help-redaction">{nRemoved > 0 ? t('askhelp.will_remove', { count: nRemoved, q: preview.text }) : ''}</p>
        <div className="row wrap" style={{ gap: 6 }}>{(['ex1', 'ex2', 'ex3'] as const).map((k) => <button key={k} type="button" className="chip toggle" onClick={() => void ask(t(`askhelp.${k}`))}>{t(`askhelp.${k}`)}</button>)}</div>
        <p id="askhelp-disclosure" className="tiny muted" style={{ marginBottom: 0 }}>{ai.ok ? t('askhelp.disclosure') : ''}</p>
      </form>
      <div className="askhelp-log" aria-live="polite">
        {turns.map((x) => <Answer key={x.id} turn={x} />)}
      </div>
      <p className="tiny muted askhelp-data">{t('askhelp.data_hint')} <button type="button" className="btn btn-sm btn-ghost" onClick={() => { if (inModal) rt.close(); navigate('/assistant'); }}>{t('askhelp.data_go')}</button></p>
    </div>
  );

  function Answer({ turn }: { turn: Turn }) {
    const results = turn.ai || turn.reason === 'health' ? [] : searchArticles(turn.q, lang, (k) => t(k)).slice(0, 5).map((a) => `a:${a.id}`);
    const refs = turn.ai ? turn.refs : [...new Set([...turn.refs, ...results])].slice(0, 6);
    const go = (path: string) => { if (inModal) rt.close(); navigate(path); };
    return (
      <article className="card card-pad askhelp-turn" data-testid="ask-help-answer" aria-busy={turn.pending || undefined}>
        <p className="askhelp-q"><b>{t('askhelp.you')}:</b> {turn.q}</p>
        {turn.removed > 0 && <p className="tiny muted" style={{ margin: '0 0 6px' }}>{t('askhelp.removed', { count: turn.removed })}</p>}
        {turn.pending ? <p className="muted small">{t('askhelp.thinking')}</p> : <>
          {turn.ai ? <div className="askhelp-a"><span className="chip askhelp-ai" title={t('askhelp.disclosure')}>{t('askhelp.ai_badge')}</span>{turn.answer.replace(/\s*\[\d\](?=[\s.,;:!?)]|$)/g, '').split(/\n{2,}/).map((p, i) => <p key={i}>{p}</p>)}</div>
            : <p className="small" data-testid="ask-help-fallback">{turn.reason ? t(`askhelp.off_${turn.reason}`) : t('askhelp.search_title')}</p>}
          {refs.length ? <div className="askhelp-refs">
            <div className="tiny upper muted">{t('askhelp.from_help')}</div>
            <ul className="guide-list" role="list">
              {refs.map((ref) => { const tg = refTarget(ref); if (!tg) return null; return (
                <li key={ref} className="askhelp-ref">
                  <button type="button" className="guide-item" onClick={() => go(tg.path)} data-testid={`ask-help-ref-${ref}`}>
                    <span className="guide-item-icon" aria-hidden="true"><Icon name={tg.kind === 'lesson' ? 'presentation' : tg.kind === 'new' ? 'spark' : 'note'} size={16} /></span>
                    <span className="grow"><b>{refTitle(ref, lang)}</b><span className="tiny muted">{t(`askhelp.kind_${tg.kind}`)}</span></span>
                  </button>
                  {tg.screens.length > 0 && <div className="row wrap" style={{ gap: 6, margin: '4px 0 0 32px' }}>{tg.screens.map((s) => <button key={s.path} type="button" className="btn btn-sm" onClick={() => go(s.path)}><Icon name="arrow" size={14} />{t('askhelp.open_screen', { label: t(s.label) })}</button>)}</div>}
                </li>); })}
            </ul>
          </div> : !turn.ai && turn.reason !== 'health' && <p className="muted small" style={{ margin: 0 }}>{t('askhelp.no_results', { q: turn.q })}</p>}
        </>}
      </article>
    );
  }
}

/** the launcher: a window over any screen (top bar, Ctrl+J / ⌘J) */
export const openAskHelp = (rt: Runtime) => rt.open(
  <Modal wide title={<span className="row" style={{ gap: 8 }}><Icon name="chat" />{rt.t('askhelp.title')} <span className="chip askhelp-ai">{rt.t('askhelp.ai_badge')}</span></span>}
    footer={<><button className="btn btn-ghost" onClick={() => { rt.close(); navigate('/help?tab=ask'); }}>{rt.t('help.open_centre')}</button><span className="spacer" /><span className="tiny muted">{rt.t('askhelp.shortcut')}</span></>}>
    <AskHelp inModal />
  </Modal>,
);

/** Settings: the help assistant on / off for the workspace (default on; it works only when the AI is available) */
export function HelpAssistantCard() {
  const rt = useRuntime();
  const { t } = rt;
  const a = useAccount();
  const on = getSetting<boolean>(rt.view, HELP_ASSISTANT_KEY, true) !== false;
  const ai = useHelpAi();
  return (
    <section className="card card-pad" style={{ marginTop: 18 }} data-testid="help-assistant-settings">
      <div className="section-title">{t('askhelp.settings_title')}</div>
      <p className="small muted" style={{ marginTop: 0 }}>{t('askhelp.settings_intro')}</p>
      <label className="check"><input type="checkbox" checked={on} onChange={(e: { target: HTMLInputElement }) => { const v = e.target.checked; void rt.run((w, c) => saveSetting(w, HELP_ASSISTANT_KEY, v, c), 'settings.saved', undefined, { undoable: true }); }} data-testid="help-assistant-enabled" />{t('askhelp.settings_enabled')}</label>
      <p className="tiny muted" style={{ marginBottom: 0 }}>{!a?.signedIn ? t('askhelp.settings_need_account') : on ? (ai.ok ? t('askhelp.settings_ai_on') : ai.ok === false ? t('askhelp.settings_ai_off') : '') : ''}</p>
    </section>
  );
}
